import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import classNames from 'classnames';
import type { Theme } from '@shared/components/RichTextToReact/types';

export type LightRetouchRichTextThemeOptions = {
    widthClassName?: string;
    textColorClassName?: string;
    hyperlinkColor?: string;
    linkFocusBorderClass?: string;
    paragraphTextColorClassName?: string;
};

/**
 * Light Retouch typography theme for Contentful rich text (`RichEditorWrapper` / `richEditorTheme`).
 *
 * | Block | Mobile | Desktop |
 * |---|---|---|
 * | H1 | 40px / normal / leading-none | 56px |
 * | H2 | 32px / normal / leading-none | 40px |
 * | H3 | 28px / normal / leading-none, mt-10 | 32px, mt-20 |
 * | H4 | 20px / normal / leading-none, mt-10 | 24px, mt-20 |
 * | Paragraph | 16px / 120%, max-w-[50rem] | same |
 * | UL / OL | 16px / 120% | same |
 * | Hyperlink / entry hyperlink | brand gold, no forced case | same |
 */
export function LightRetouchRichTextTheme(options: LightRetouchRichTextThemeOptions = {}): Theme {
    const {
        widthClassName,
        textColorClassName,
        hyperlinkColor,
        linkFocusBorderClass = '',
        paragraphTextColorClassName,
    } = options;

    // One colour class per block. A second text-* utility ties with the first
    // and the later token in the Tailwind theme wins, so overrides never apply.
    const headingColor = textColorClassName ?? 'text-colourContainerTertiary';
    const bodyColor =
        paragraphTextColorClassName ?? textColorClassName ?? 'text-colourForegroundSecondary';
    const linkColor = hyperlinkColor ?? 'text-brandRefinedGold';
    const linkClassName = classNames(
        linkColor,
        linkFocusBorderClass,
        // No inline-block/p-1 - they inflate the parent line-box when `a` is inside `p`.
        'font-medium normal-case underline-offset-4 transition-colors hover:underline focus:outline-none focus-visible:rounded focus-visible:outline focus-visible:outline-[1.5px] focus-visible:outline-offset-2',
    );

    return {
        [BLOCKS.HEADING_1]: classNames(
            widthClassName,
            headingColor,
            'mb-5 mt-0 p-0 text-[40px] font-normal normal-case leading-none last:!mb-0 md:text-[56px]',
        ),
        [BLOCKS.HEADING_2]: classNames(
            widthClassName,
            headingColor,
            'mb-5 mt-0 p-0 text-[32px] font-normal normal-case leading-none last:!mb-0 md:text-[40px]',
        ),
        [BLOCKS.HEADING_3]: classNames(
            widthClassName,
            headingColor,
            'mb-5 p-0 text-[28px] font-normal normal-case leading-none last:!mb-0 md:text-[32px]',
        ),
        [BLOCKS.HEADING_4]: classNames(
            widthClassName,
            headingColor,
            'mb-5 p-0 text-[20px] font-normal normal-case leading-none last:!mb-0 md:text-[24px]',
        ),
        [BLOCKS.PARAGRAPH]: classNames(
            widthClassName,
            bodyColor,
            // Keep break-spaces for intentional soft breaks; edge `\n` trimmed in RichTextToReact.
            // Avoid `text-base` (ships line-height: 1.5rem).
            'mb-5 whitespace-break-spaces text-[16px] font-normal normal-case !leading-[1.2] last:!mb-0',
        ),
        [BLOCKS.UL_LIST]: classNames(
            widthClassName,
            bodyColor,
            'mb-5 list-disc pl-5 text-left text-[16px] font-normal normal-case !leading-[1.2] last:!mb-0 [&>li]:mt-2 [&>li>p]:inline',
        ),
        [BLOCKS.OL_LIST]: classNames(
            widthClassName,
            bodyColor,
            'mb-5 list-decimal pl-5 text-left text-[16px] font-normal normal-case !leading-[1.2] last:!mb-0 [&>li]:mt-2 [&>li>p]:inline',
        ),
        [INLINES.HYPERLINK]: linkClassName,
        [INLINES.ENTRY_HYPERLINK]: linkClassName,
    };
}
